import { useEffect, useMemo, useRef, useState } from 'react';
import { elementColor, BY_SYMBOL } from './elements';
import { percentComposition } from './formula';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from './vizShared';

// PercentComposition — donut + bar of element mass-% in a compound. Either pass
// explicit parts, or just a compound formula and let it compute the composition.
// config: { compound, parts?:[{symbol,percent}], title? }. Resilient: if neither
// is usable, shows an empty state.

export interface PercentCompositionConfig {
  title?: string;
  compound?: string; // formula, e.g. "H2O"
  parts?: Array<{ symbol?: string; percent?: number; label?: string }>;
}

interface Props {
  config?: PercentCompositionConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface Slice {
  label: string;
  symbol: string;
  percent: number;
  color: string;
}

const SIZE = 180;
const C = SIZE / 2;
const RADIUS = 70;
const STROKE = 28;

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polar(cx, cy, r, endDeg);
  const end = polar(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export default function PercentComposition({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const [active, setActive] = useState<number | null>(null);
  // grow: 0..1 multiplier applied to each arc sweep + each bar width.
  const demoEnabled = !reducedMotion && !cueOn;
  const [grow, setGrow] = useState(demoEnabled ? 0 : 1);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const demoActiveRef = useRef(demoEnabled);

  const slices: Slice[] = useMemo(() => {
    // Prefer explicit parts; otherwise compute from the compound formula.
    if (config.parts && config.parts.length > 0) {
      const raw = config.parts
        .map((p) => ({
          symbol: p.symbol ?? '',
          label: p.label ?? (p.symbol ? BY_SYMBOL[p.symbol]?.name ?? p.symbol : 'Other'),
          percent: typeof p.percent === 'number' ? p.percent : 0,
        }))
        .filter((p) => p.percent > 0);
      const sum = raw.reduce((s, p) => s + p.percent, 0) || 1;
      return raw.map((p) => ({ ...p, percent: (p.percent / sum) * 100, color: elementColor(p.symbol) }));
    }
    if (config.compound) {
      return percentComposition(config.compound).map((p) => ({
        symbol: p.symbol,
        label: BY_SYMBOL[p.symbol]?.name ?? p.symbol,
        percent: p.percent,
        color: elementColor(p.symbol),
      }));
    }
    return [];
  }, [config.parts, config.compound]);

  // --- Self-demo: grow arcs/bars 0 → 1 over ~1s, then "Now you try". ----------
  useEffect(() => {
    if (!demoEnabled) {
      setGrow(1);
      return;
    }
    demoActiveRef.current = true;
    const DURATION = 1000;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      if (!demoActiveRef.current) return;
      const t = Math.max(0, Math.min(1, (now - start) / DURATION));
      const e = 1 - Math.pow(1 - t, 3); // ease-out
      setGrow(e);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        demoActiveRef.current = false;
        setGrow(1);
        setShowTry(true);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      demoActiveRef.current = false;
      cancelAnimationFrame(raf);
    };
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Cue: highlight a symbol → spotlight slice; progress → grow position. ----
  useEffect(() => {
    if (!cue) return;
    demoActiveRef.current = false;
    setShowTry(false);
    if (typeof cue.progress === 'number') {
      setGrow(Math.max(0, Math.min(1, cue.progress)));
    } else {
      setGrow(1);
    }
    const tokens = cueHighlights(cue);
    if (tokens.length > 0) {
      const idx = slices.findIndex((s) => s.symbol.toLowerCase() === tokens[0]);
      if (idx >= 0) setActive(idx);
    }
  }, [cue, slices]);

  const cancelDemo = () => {
    if (demoActiveRef.current) {
      demoActiveRef.current = false;
      setGrow(1);
    }
  };

  const handleActivate = (i: number) => {
    cancelDemo();
    setShowTry(false);
    setActive((cur) => (cur === i ? null : i));
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'percent_composition', compound: config.compound ?? null });
    }
  };

  if (slices.length === 0) {
    return (
      <VizCard title={config.title ?? 'Percent composition'} reducedMotion={reducedMotion}>
        <div className="rounded-md border border-ws-border bg-ws-surface-2 p-4 text-sm text-ws-ink-3">
          No composition data to display.
        </div>
      </VizCard>
    );
  }

  // Build donut arcs (scaled by the grow multiplier).
  let cursor = 0;
  const arcs = slices.map((s, i) => {
    const sweep = (s.percent / 100) * 360 * grow;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    return { ...s, start, end, i };
  });

  return (
    <VizCard
      eyebrow="Mass composition"
      title={config.title ?? 'Percent composition'}
      subtitle={config.compound ? <span className="font-mono">{config.compound}</span> : undefined}
      reducedMotion={reducedMotion}
      hint="Hover or click a slice to spotlight that element"
    >
      <div className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <NowYouTry show={showTry} />
        {/* Donut */}
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-40 flex-shrink-0" role="img" aria-label="Composition donut">
          {/* track ring */}
          <circle cx={C} cy={C} r={RADIUS} fill="none" stroke="var(--ws-border)" strokeWidth={STROKE} opacity={0.35} />
          {arcs.map((a) => (
            <path
              key={a.symbol}
              d={arcPath(C, C, RADIUS, a.start, a.end <= a.start ? a.start + 0.01 : a.end)}
              fill="none"
              stroke={a.color}
              strokeWidth={active === a.i ? STROKE + 6 : STROKE}
              strokeLinecap="butt"
              className="cursor-pointer transition-all"
              onMouseEnter={() => setActive(a.i)}
              onMouseLeave={() => setActive((cur) => (cur === a.i ? null : cur))}
              onClick={() => handleActivate(a.i)}
            />
          ))}
          {/* Center label */}
          <text x={C} y={C - 2} textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--ws-ink)">
            {active != null ? `${slices[active].percent.toFixed(1)}%` : '100%'}
          </text>
          <text x={C} y={C + 13} textAnchor="middle" fontSize="9" fill="var(--ws-ink-3)">
            {active != null ? slices[active].symbol : 'by mass'}
          </text>
        </svg>

        {/* Bars + legend */}
        <div className="flex w-full min-w-0 flex-1 flex-col gap-2">
          {arcs.map((a) => (
            <button
              key={a.symbol}
              type="button"
              onClick={() => handleActivate(a.i)}
              onMouseEnter={() => setActive(a.i)}
              onMouseLeave={() => setActive((cur) => (cur === a.i ? null : cur))}
              className={`group flex items-center gap-2 rounded-sm px-1.5 py-1 text-left transition-colors ${VIZ_FOCUS} ${
                active === a.i ? 'bg-ws-surface-2' : 'hover:bg-ws-surface-2'
              }`}
            >
              <span className="inline-block h-3 w-3 flex-shrink-0 rounded-[2px]" style={{ backgroundColor: a.color }} />
              <span className="w-24 flex-shrink-0 truncate text-xs text-ws-ink-2">{a.label}</span>
              <span className="relative h-3 flex-1 overflow-hidden rounded-full bg-ws-border">
                <span
                  className="absolute left-0 top-0 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${a.percent * grow}%`, backgroundColor: a.color }}
                />
              </span>
              <span className="w-12 flex-shrink-0 text-right text-xs font-semibold tabular-nums text-ws-ink">
                {a.percent.toFixed(1)}%
              </span>
            </button>
          ))}
        </div>
      </div>
    </VizCard>
  );
}
